/**
 * 판정 기록 요약 organism — 처방 수·byGrade 7칸·상위 룰(클릭 채움)·정오 진행률 + 로딩/오류.
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * byGrade 칸 제목은 목록 배지와 같은 상수(GRADE_LABELS·PURGED_LABEL) — L1248. 상위 룰 클릭 → onSelectRule.
 */
import React from "react";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import type { DoseReviewLogSummaryData, DoseGrade } from "services/api/doseReviewLogs/types";
import {
  GRADE_ORDER,
  GRADE_LABELS,
  PURGED_LABEL,
  labelProgressSummary,
  LIST_ERROR_TEXT,
  RETRY_LABEL,
} from "utils/doseReviewLogs";
import {
  Wrap,
  Total,
  GradeGrid,
  GradeCell,
  GradeCount,
  RulesWrap,
  SectionLabel,
  Progress,
  StateBox,
} from "./style";

export interface DoseReviewLogSummaryProps {
  isLoading: boolean;
  isError: boolean;
  onRetry?: () => void;
  data?: DoseReviewLogSummaryData;
  onSelectRule: (ruleId: string) => void;
}

const DoseReviewLogSummary: React.FC<DoseReviewLogSummaryProps> = ({
  isLoading,
  isError,
  onRetry,
  data,
  onSelectRule,
}) => {
  if (isError) {
    return (
      <StateBox data-testid="dose-log-summary-error">
        <span>{LIST_ERROR_TEXT}</span>
        {onRetry && (
          <Button buttonType="button" type="Text" size="XSmall" variant="Outlined" color="Secondary" onClick={onRetry}>
            {RETRY_LABEL}
          </Button>
        )}
      </StateBox>
    );
  }
  if (isLoading || !data) {
    return (
      <StateBox data-testid="dose-log-summary-loading">
        <Spinner />
      </StateBox>
    );
  }

  return (
    <Wrap data-testid="dose-log-summary">
      <Total>처방 {data.prescriptionCount}건</Total>
      <GradeGrid>
        {GRADE_ORDER.map((g: DoseGrade) => (
          <GradeCell key={g} data-testid={`dose-summary-grade-${g}`}>
            <span>{GRADE_LABELS[g]}</span>
            <GradeCount>{data.byGrade[g]}</GradeCount>
          </GradeCell>
        ))}
        <GradeCell data-testid="dose-summary-grade-purged">
          <span>{PURGED_LABEL}</span>
          <GradeCount>{data.byGrade.purged}</GradeCount>
        </GradeCell>
      </GradeGrid>

      <div>
        <SectionLabel>상위 룰</SectionLabel>
        <RulesWrap>
          {data.topRules.length === 0 && <Progress>발화 룰 없음</Progress>}
          {data.topRules.map((r) => (
            <Button
              key={r.ruleId}
              buttonType="button"
              type="Text"
              size="XSmall"
              variant="Tinted"
              color="Secondary"
              onClick={() => onSelectRule(r.ruleId)}
              data-testid="dose-summary-rule"
            >
              #{r.ruleId} ({r.prescriptionCount})
            </Button>
          ))}
        </RulesWrap>
      </div>

      <Progress data-testid="dose-summary-label-progress">
        {labelProgressSummary(data.labels.labeledItems, data.labels.labelableItems)}
      </Progress>
    </Wrap>
  );
};

export default DoseReviewLogSummary;
